import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, RefreshCw, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { API_BASE_URL, API_ENDPOINTS, REQUEST_CONFIG } from '../config';
import { 
  TestHeader, 
  TestDescription, 
  TestApiCalls, 
  TestResources, 
  TestRemediation,
  NoData
} from './testDetails';

const SecurityTestDetails = ({ isOpen, onClose, testId, testName, category }) => {
  const [testDetails, setTestDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRunningTest, setIsRunningTest] = useState(false);

  // Fetch test details on component mount
  useEffect(() => {
    if (isOpen && testId) {
      fetchTestDetails();
    }
  }, [isOpen, testId]);

  // Function to fetch test details
  const fetchTestDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}${API_ENDPOINTS.INFRASTRUCTURE.TEST_DETAILS}/${testId}`,
        { 
          headers: REQUEST_CONFIG.getAuthHeaders(),
          timeout: REQUEST_CONFIG.timeout
        }
      );
      setTestDetails(response.data);
    } catch (err) {
      console.error(`Error fetching test details for ${testId}:`, err);
      if (err.response && err.response.status === 404) {
        setError('No test data found. Please run the security tests first.');
      } else if (err.code === 'ERR_NETWORK') {
        setError('Network error. Please make sure the security API server is running.');
      } else {
        setError('Failed to load test details. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to run the specific test
  const runTest = async () => {
    setIsRunningTest(true);
    try {
      await axios.post(
        `${API_BASE_URL}${API_ENDPOINTS.INFRASTRUCTURE.RUN_TESTS}`,
        {},
        { 
          headers: REQUEST_CONFIG.getAuthHeaders(),
          timeout: REQUEST_CONFIG.timeout
        }
      );
      
      // Check if this is a log management test or vulnerability scanning test
      if (
        testId === 'vpc-flow-logs' || 
        testId === 'cloud-trail' || 
        testId === 'log-retention' || 
        testId === 's3-server-access-logs' ||
        testId === 'vulnerability-scanning' ||
        testId === 'aws-inspector'
      ) {
        // Reload the page to refresh all data
        window.location.reload();
      } else {
        // For other tests, just fetch the details again
        await fetchTestDetails();
        setIsRunningTest(false);
      }
    } catch (err) {
      console.error('Error running security tests:', err);
      setError('Failed to run security tests. Please try again later.');
      setIsRunningTest(false);
    }
  };

  // Helper function to extract bullet points from description
  const extractBulletPoints = (description) => {
    const bulletPoints = [];
    
    if (testId === 'vulnerability-scanning') {
      bulletPoints.push(
        'Identify security vulnerabilities in your container images',
        'Detect outdated software packages and common vulnerabilities and exposures (CVEs)',
        'Prioritize remediation efforts based on severity scores',
        'Continuously monitor your repositories for new vulnerabilities',
        'Improve your overall security posture'
      );
    } else if (testId === 'aws-inspector') {
      bulletPoints.push(
        'Automatically scan AWS workloads for vulnerabilities',
        'Detect software vulnerabilities in EC2 instances, container images, and Lambda functions',
        'Identify unintended network exposure of your resources',
        'Receive prioritized security findings with remediation guidance',
        'Continuously monitor your environment for new vulnerabilities'
      );
    } else if (testId === 'vpc-flow-logs') {
      bulletPoints.push(
        'Track network traffic to and from your VPC resources',
        'Detect suspicious activity and potential security threats',
        'Troubleshoot connectivity issues and network performance',
        'Meet compliance requirements for network monitoring'
      );
    } else if (testId === 'cloud-trail') {
      bulletPoints.push(
        'Record API calls for your AWS account',
        'Track user activity and changes to resources',
        'Enable security analysis and troubleshooting',
        'Meet compliance requirements for audit logging'
      );
    } else if (testId === 's3-server-access-logs') {
      bulletPoints.push(
        'Record requests made to your S3 buckets',
        'Track who accessed your data and when',
        'Detect unauthorized access attempts',
        'Meet compliance requirements for data access logging'
      );
    } else if (testId === 's3-bucket-encryption') {
      bulletPoints.push(
        'Protect data at rest in your S3 buckets',
        'Automatically encrypt all objects when stored in S3',
        'Prevent unauthorized access to sensitive data',
        'Meet compliance requirements for data encryption',
        'Choose between AWS-managed keys (SSE-S3) or customer-managed keys (SSE-KMS)'
      );
    } else if (testId === 'dynamodb-encryption') {
      bulletPoints.push(
        'Protect data at rest in your DynamoDB tables',
        'Automatically encrypt all data when stored in DynamoDB',
        'Prevent unauthorized access to sensitive data',
        'Meet compliance requirements for data encryption',
        'Choose between AWS-owned keys, AWS-managed keys (AWS KMS), or customer-managed keys (CMK)'
      );
    } else if (testId === 'ebs-encryption') {
      bulletPoints.push(
        'Protect data at rest in your EBS volumes',
        'Automatically encrypt all data when stored on EBS volumes',
        'Prevent unauthorized access to sensitive data',
        'Meet compliance requirements for data encryption',
        'Secure snapshots created from encrypted volumes',
        'Secure volumes created from encrypted snapshots'
      );
    } else if (testId === 'log-retention') {
      bulletPoints.push(
        'Ensure logs are retained for an appropriate period',
        'Comply with regulatory requirements for log retention',
        'Maintain historical data for security investigations',
        'Optimize storage costs by automatically expiring old logs'
      );
    } else if (testId === 'mfa-root-account') {
      bulletPoints.push(
        'Protect your root AWS account from unauthorized access',
        'Add an additional layer of security beyond password protection',
        'Prevent account compromise even if password is exposed',
        'Meet security best practices and compliance requirements',
        'Safeguard the most privileged account in your AWS environment'
      );
    } else if (testId === 'mfa-iam-users') {
      bulletPoints.push(
        'Protect all IAM users with console access from unauthorized access',
        'Add an additional layer of security beyond password protection',
        'Prevent account compromise even if passwords are exposed',
        'Meet security best practices and compliance requirements',
        'Enforce consistent security controls across all user accounts'
      );
    } else if (testId === 'mfa-org-users') {
      bulletPoints.push(
        'Protect all AWS Organizations users with console access from unauthorized access',
        'Add an additional layer of security beyond password protection',
        'Prevent account compromise even if passwords are exposed',
        'Meet security best practices and compliance requirements',
        'Enforce consistent security controls across your entire AWS organization',
        'Strengthen security posture across all accounts in your organization'
      );
    } else if (testId === 'iam-user-policies') {
      bulletPoints.push(
        'Follow AWS IAM best practices for permission management',
        'Simplify access control by using groups instead of direct policy attachments',
        'Reduce the risk of excessive permissions for individual users',
        'Make it easier to audit and manage access controls',
        'Ensure consistent permissions for users with similar roles',
        'Improve security posture through proper IAM configuration'
      );
    } else if (testId === 'https-redirect') {
      bulletPoints.push(
        'Ensure all HTTP traffic is automatically redirected to HTTPS',
        'Protect sensitive data in transit from eavesdropping and tampering',
        'Prevent man-in-the-middle attacks',
        'Meet compliance requirements for secure data transmission',
        'Provide a better user experience with secure connections'
      );
    } else if (testId === 'guard-duty') {
      bulletPoints.push(
        'Continuously monitor for malicious activity and unauthorized behavior',
        'Detect threats using machine learning and anomaly detection',
        'Analyze events from CloudTrail, VPC Flow Logs, and DNS logs',
        'Receive detailed security findings with remediation guidance',
        'Protect your AWS accounts, workloads, and data from threats',
        'Identify suspicious API calls, potentially compromised instances, and reconnaissance attempts'
      );
    } else if (testId === 'security-hub') {
      bulletPoints.push(
        'Automate security checks against best practices and standards',
        'Aggregate security findings from multiple AWS services and partner tools',
        'Centralize security alerts and compliance status in one place',
        'Prioritize security findings based on severity',
        'Continuously monitor your AWS environment for security issues',
        'Enable automated remediation for security findings'
      );
    } else {
      return [];
    }
    
    return bulletPoints;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">{testDetails?.name || testName}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        
        {/* Modal Content */}
        <div className="overflow-y-auto flex-grow">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw size={24} className="animate-spin text-blue-600 mr-2" />
              <span>Loading test details...</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md m-4 p-4 text-red-700">
              <div className="flex items-center mb-2">
                <AlertCircle size={20} className="mr-2" />
                <span className="font-bold">Error</span>
              </div>
              <p>{error}</p>
              <button
                onClick={runTest}
                disabled={isRunningTest}
                className="mt-4 flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors disabled:bg-blue-400"
              >
                <RefreshCw size={16} className={`mr-2 ${isRunningTest ? 'animate-spin' : ''}`} />
                {isRunningTest ? 'Running Test...' : 'Run Test Now'}
              </button>
            </div>
          ) : testDetails ? (
            <>
              <TestHeader 
                testName={testDetails.name}
                status={testDetails.status}
                lastChecked={testDetails.last_checked}
                category={testDetails.category}
              />
              
              <TestDescription 
                description={testDetails.description}
                bulletPoints={extractBulletPoints(testDetails.description)}
              />
              
              <TestApiCalls 
                apiCalls={testDetails.api_calls || []}
                testId={testId}
              />
              
              <TestResources 
                testDetails={testDetails.details}
                testId={testId}
              />
              
              <TestRemediation 
                remediationSteps={testDetails.remediation_steps || []}
              />
            </>
          ) : (
            <NoData 
              runTest={runTest}
              isRunningTest={isRunningTest}
            />
          )}
        </div>
        
        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityTestDetails;
